import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, LogOut, FileText, Tag, 
  Eye, EyeOff, Calendar, Search, AlertCircle, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [searchQuery, setSearchQuery] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [addingCategory, setAddingCategory] = useState(false);

  useEffect(() => {
    // Check if logged in
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin !== 'true') {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const password = sessionStorage.getItem('adminPassword');
    return {
      'Authorization': 'Basic ' + btoa(`admin:${password}`),
      'Content-Type': 'application/json'
    };
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchPosts(), fetchCategories()]);
    setLoading(false);
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts?published_only=false`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('isAdmin');
    sessionStorage.removeItem('adminPassword');
    navigate('/admin');
  };

  const handleDeletePost = async (postId, postTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${postTitle}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Post deleted successfully' });
        fetchPosts();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete post' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting post' });
    }

    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleAddCategory = async (e) => {
    console.log('handleAddCategory called', { newCategory, e });
    
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!newCategory.trim()) {
      setMessage({ type: 'error', text: 'Please enter a category name' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setAddingCategory(true);
    
    try {
      console.log('Making API request to add category:', newCategory.trim());
      const response = await fetch(`${API_URL}/api/blog/categories`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: newCategory.trim() })
      });

      console.log('API response status:', response.status);
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Category added successfully' });
        setNewCategory('');
        fetchCategories();
      } else {
        const error = await response.json();
        console.log('API error:', error);
        setMessage({ type: 'error', text: error.detail || 'Failed to add category' });
      }
    } catch (error) {
      console.error('Error adding category:', error);
      setMessage({ type: 'error', text: 'Error adding category: ' + error.message });
    } finally {
      setAddingCategory(false);
    }

    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? Posts in this category will be uncategorized.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/blog/categories/${categoryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Category deleted successfully' });
        fetchCategories();
        fetchPosts();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete category' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting category' });
    }

    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#0a4447',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#C9A227'
            }}>
              Resolv Digital Admin
            </h1>
            <Link
              to="/blog"
              target="_blank"
              style={{
                color: '#b8d8da',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                textDecoration: 'none'
              }}
            >
              View Blog →
            </Link>
          </div>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '8px 16px',
              borderRadius: '6px',
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            data-testid="admin-logout-button"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 24px'
      }}>
        {/* Message */}
        {message.text && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(220, 53, 69, 0.1)',
            border: `1px solid ${message.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(220, 53, 69, 0.3)'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '24px'
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={18} style={{ color: '#10b981' }} />
            ) : (
              <AlertCircle size={18} style={{ color: '#dc3545' }} />
            )}
            <span style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              color: message.type === 'success' ? '#10b981' : '#dc3545'
            }}>{message.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('posts')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              backgroundColor: activeTab === 'posts' ? '#0D7377' : 'transparent',
              color: activeTab === 'posts' ? '#ffffff' : '#6b7280',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <FileText size={18} /> Posts ({posts.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              backgroundColor: activeTab === 'categories' ? '#0D7377' : 'transparent',
              color: activeTab === 'categories' ? '#ffffff' : '#6b7280',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <Tag size={18} /> Categories ({categories.length})
          </button>
        </div>

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div>
            {/* Actions Bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                position: 'relative',
                flex: '1 1 300px',
                maxWidth: '400px'
              }}>
                <Search size={18} style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 44px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    color: '#111827',
                    backgroundColor: '#ffffff'
                  }}
                  data-testid="admin-search-posts"
                />
              </div>
              <Link
                to="/admin/posts/new"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#C9A227',
                  color: '#0a4447',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#b8922a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#C9A227'}
                data-testid="admin-new-post-button"
              >
                <Plus size={18} /> New Post
              </Link>
            </div>

            {/* Posts Table */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <p style={{ color: '#6b7280' }}>Loading posts...</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <FileText size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: '#374151',
                  marginBottom: '8px'
                }}>No posts found</h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#6b7280',
                  marginBottom: '24px'
                }}>
                  {searchQuery ? 'Try a different search term.' : 'Create your first blog post to get started.'}
                </p>
                {!searchQuery && (
                  <Link
                    to="/admin/posts/new"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: '#0D7377',
                      color: '#ffffff',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontFamily: 'Lato, sans-serif',
                      fontWeight: 600
                    }}
                  >
                    <Plus size={18} /> Create Post
                  </Link>
                )}
              </div>
            ) : (
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{
                        padding: '14px 20px',
                        textAlign: 'left',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb'
                      }}>Title</th>
                      <th style={{
                        padding: '14px 20px',
                        textAlign: 'left',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb'
                      }}>Category</th>
                      <th style={{
                        padding: '14px 20px',
                        textAlign: 'left',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb'
                      }}>Status</th>
                      <th style={{
                        padding: '14px 20px',
                        textAlign: 'left',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb'
                      }}>Date</th>
                      <th style={{
                        padding: '14px 20px',
                        textAlign: 'right',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPosts.map((post) => (
                      <tr 
                        key={post.id}
                        style={{ transition: 'background-color 0.2s ease' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <div>
                            <p style={{
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              color: '#111827',
                              marginBottom: '4px'
                            }}>{post.title}</p>
                            <p style={{
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.8rem',
                              color: '#6b7280'
                            }}>{post.excerpt.substring(0, 60)}...</p>
                          </div>
                        </td>
                        <td style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {post.category_name ? (
                            <span style={{
                              backgroundColor: 'rgba(13, 115, 119, 0.1)',
                              color: '#0D7377',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>{post.category_name}</span>
                          ) : (
                            <span style={{
                              color: '#9ca3af',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.85rem'
                            }}>—</span>
                          )}
                        </td>
                        <td style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {post.published ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: 'rgba(16, 185, 129, 0.1)',
                              color: '#10b981',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              <Eye size={12} /> Published
                            </span>
                          ) : (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: 'rgba(107, 114, 128, 0.1)',
                              color: '#6b7280',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              <EyeOff size={12} /> Draft
                            </span>
                          )}
                        </td>
                        <td style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: '#6b7280',
                            fontFamily: 'Lato, sans-serif',
                            fontSize: '0.85rem'
                          }}>
                            <Calendar size={14} /> {formatDate(post.created_at)}
                          </span>
                        </td>
                        <td style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #e5e7eb',
                          textAlign: 'right'
                        }}>
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'flex-end'
                          }}>
                            <Link
                              to={`/admin/posts/${post.id}/edit`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px',
                                backgroundColor: 'rgba(13, 115, 119, 0.1)',
                                color: '#0D7377',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#0D7377';
                                e.currentTarget.style.color = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(13, 115, 119, 0.1)';
                                e.currentTarget.style.color = '#0D7377';
                              }}
                              title="Edit post"
                            >
                              <Edit2 size={16} />
                            </Link>
                            <button
                              onClick={() => handleDeletePost(post.id, post.title)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                color: '#dc3545',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                                e.currentTarget.style.color = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                                e.currentTarget.style.color = '#dc3545';
                              }}
                              title="Delete post"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div>
            {/* Add Category Form */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
              maxWidth: '500px'
            }}>
              <input
                type="text"
                placeholder="New category name..."
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCategory(e);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  outline: 'none',
                  color: '#111827',
                  backgroundColor: '#ffffff'
                }}
                data-testid="admin-new-category-input"
              />
              <button
                type="button"
                onClick={() => handleAddCategory()}
                disabled={addingCategory}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: addingCategory ? '#9ca3af' : '#C9A227',
                  color: '#0a4447',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: addingCategory ? 'wait' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
                data-testid="admin-add-category-button"
              >
                <Plus size={18} /> {addingCategory ? 'Adding...' : 'Add'}
              </button>
            </div>

            {/* Categories List */}
            {categories.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <Tag size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: '#374151',
                  marginBottom: '8px'
                }}>No categories yet</h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#6b7280'
                }}>Add categories to organise your blog posts.</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                {categories.map((category) => (
                  <div
                    key={category.id}
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <h4 style={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '4px'
                      }}>{category.name}</h4>
                      <p style={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.8rem',
                        color: '#9ca3af'
                      }}>/{category.slug}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteCategory(category.id, category.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        backgroundColor: 'transparent',
                        color: '#9ca3af',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                        e.currentTarget.style.color = '#dc3545';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#9ca3af';
                      }}
                      title="Delete category"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
